import React from "react";
import { HelpCircle, BarChart2 } from "lucide-react";
import Grid from "./Grid";
import Keyboard from "./Keyboard";
import DivisibilityInfo from "./DivisibilityInfo";
import HowToPlay from "./HowToPlay";
import ResultModal from "./ResultModal";
import Footer from "./Footer";
import { getPlayerId, startSession, submitGuess } from "./api";

const STAGGER = 300;
const ONBOARD_KEY = "numex_onboarded";

export default function NumexGame() {
  const [puzzle, setPuzzle] = React.useState(null);
  const [guesses, setGuesses] = React.useState([]);
  const [currentGuess, setCurrentGuess] = React.useState("");
  const [status, setStatus] = React.useState("loading"); // loading|playing|won|lost
  const [revealRow, setRevealRow] = React.useState(null);
  const [message, setMessage] = React.useState("");
  const [shakeRow, setShakeRow] = React.useState(null);
  const [answer, setAnswer] = React.useState(null);
  const [stats, setStats] = React.useState(null);
  const [howToOpen, setHowToOpen] = React.useState(false);
  const [resultOpen, setResultOpen] = React.useState(false);
  const [ruleOpen, setRuleOpen] = React.useState(false);

  const playerIdRef = React.useRef(null);
  const busyRef = React.useRef(false);
  const msgTimer = React.useRef(null);
  const shakeTimer = React.useRef(null);

  // Load / restore today's session from the server (authoritative).
  React.useEffect(() => {
    const pid = getPlayerId();
    playerIdRef.current = pid;
    startSession(pid)
      .then((data) => {
        setPuzzle(data.puzzle);
        setGuesses(data.guesses || []);
        setStatus(data.status);
        setAnswer(data.answer || null);
        setStats(data.stats || null);
      })
      .catch(() => setMessage("Couldn't load today's puzzle."));
  }, []);

  // First-visit onboarding (separate from game state).
  React.useEffect(() => {
    let seen = false;
    try {
      seen = localStorage.getItem(ONBOARD_KEY) === "1";
    } catch (e) {
      /* ignore */
    }
    if (!seen) setHowToOpen(true);
  }, []);

  const dismissOnboarding = React.useCallback(() => {
    try {
      localStorage.setItem(ONBOARD_KEY, "1");
    } catch (e) {
      /* ignore */
    }
    setHowToOpen(false);
  }, []);

  // When the player's local calendar day rolls over, load the new day's puzzle.
  React.useEffect(() => {
    const now = new Date();
    const midnight = new Date(now);
    midnight.setHours(24, 0, 0, 1);
    const ms = midnight - now;
    const id = setTimeout(() => window.location.reload(), ms);
    return () => clearTimeout(id);
  }, []);

  const showMessage = React.useCallback((text) => {
    setMessage(text);
    if (msgTimer.current) clearTimeout(msgTimer.current);
    msgTimer.current = setTimeout(() => setMessage(""), 2000);
  }, []);

  const triggerShake = React.useCallback((row) => {
    setShakeRow(row);
    if (shakeTimer.current) clearTimeout(shakeTimer.current);
    shakeTimer.current = setTimeout(() => setShakeRow(null), 450);
  }, []);

  const gameOver = status === "won" || status === "lost";
  const locked = busyRef.current || revealRow !== null || gameOver || status === "loading";

  const handleKey = React.useCallback(
    (d) => {
      if (locked || !puzzle) return;
      if (currentGuess.length >= puzzle.length) return;
      if (currentGuess.length === 0 && d === "0") {
        showMessage("Number can't start with 0.");
        return;
      }
      setCurrentGuess((prev) => prev + d);
    },
    [locked, puzzle, currentGuess, showMessage]
  );

  const handleDelete = React.useCallback(() => {
    if (locked) return;
    setCurrentGuess((prev) => prev.slice(0, -1));
  }, [locked]);

  const handleEnter = React.useCallback(async () => {
    if (busyRef.current || revealRow !== null || gameOver || !puzzle) return;
    const rowIndex = guesses.length;

    if (currentGuess.length < puzzle.length) {
      showMessage("Not enough digits.");
      triggerShake(rowIndex);
      return;
    }

    busyRef.current = true;
    try {
      const res = await submitGuess(playerIdRef.current, currentGuess);
      if (!res.accepted) {
        showMessage(res.message || "Invalid number.");
        triggerShake(rowIndex);
        busyRef.current = false;
        return;
      }

      const digits = currentGuess;
      const feedback = res.feedback;
      const newGuesses = [...guesses, { digits, feedback }];
      setGuesses(newGuesses);
      setCurrentGuess("");
      setRevealRow(rowIndex);

      const total = (puzzle.length - 1) * STAGGER + 600;
      setTimeout(() => {
        setRevealRow(null);
        busyRef.current = false;

        if (res.status === "won" || res.status === "lost") {
          setStatus(res.status);
          setAnswer(res.answer || null);
          setStats(res.stats || null);
          // Open the result modal shortly after the final tiles settle.
          setTimeout(() => setResultOpen(true), 350);
        }
      }, total);
    } catch (e) {
      showMessage("Something went wrong. Try again.");
      busyRef.current = false;
    }
  }, [puzzle, guesses, currentGuess, revealRow, gameOver, showMessage, triggerShake]);

  React.useEffect(() => {
    const onKeyDown = (e) => {
      if (e.metaKey || e.ctrlKey || e.altKey) return;
      if (howToOpen || resultOpen || ruleOpen) return;
      if (e.key >= "0" && e.key <= "9") {
        handleKey(e.key);
      } else if (e.key === "Backspace" || e.key === "Delete") {
        handleDelete();
      } else if (e.key === "Enter") {
        handleEnter();
      }
    };
    window.addEventListener("keydown", onKeyDown);
    return () => window.removeEventListener("keydown", onKeyDown);
  }, [handleKey, handleDelete, handleEnter, howToOpen, resultOpen, ruleOpen]);

  if (!puzzle) {
    return (
      <div className="min-h-screen flex items-center justify-center text-[var(--numex-muted)]">
        Loading…
      </div>
    );
  }

  return (
    <div className="min-h-[100dvh] flex flex-col items-center px-4 py-4 sm:py-6">
      <div className="w-full max-w-[336px] flex flex-col items-center">
        {/* Header */}
        <header className="relative w-full flex flex-col items-center" data-testid="numex-header">
          <button
            type="button"
            onClick={() => setResultOpen(true)}
            aria-label="Statistics"
            className="absolute left-0 top-0 inline-flex items-center justify-center text-[var(--numex-muted)] hover:text-[var(--numex-ink)] transition-colors"
            data-testid="stats-btn"
          >
            <BarChart2 size={20} strokeWidth={1.75} />
          </button>
          <button
            type="button"
            onClick={() => setHowToOpen(true)}
            aria-label="How to play"
            className="absolute right-0 top-0 inline-flex items-center justify-center text-[var(--numex-muted)] hover:text-[var(--numex-ink)] transition-colors"
            data-testid="help-btn"
          >
            <HelpCircle size={20} strokeWidth={1.75} />
          </button>

          <div className="font-mono text-lg font-bold leading-none tracking-tight text-[var(--numex-ink)]">
            ÷X
          </div>
          <h1 className="mt-1 text-xl font-extrabold tracking-[0.16em] pl-[0.16em]">NUMEX</h1>
          <p className="mt-0.5 text-[11px] text-[var(--numex-muted)] tracking-wide">
            5 digits. 5 tries. One number a day.
          </p>
        </header>

        {/* Board area */}
        <main className="w-full flex flex-col items-center relative mt-4">
          <div className="flex items-center gap-2 text-sm" data-testid="puzzle-meta">
            <span className="text-[var(--numex-muted)]" data-testid="puzzle-number">
              Puzzle #{puzzle.puzzle_number}
            </span>
            <span className="text-[var(--numex-tile-idle-border)]">·</span>
            <button
              type="button"
              onClick={() => setRuleOpen(true)}
              className="group inline-flex items-center gap-1.5"
              data-testid="divisor-line"
            >
              <span className="font-medium text-[var(--numex-ink)]">
                Divisible by {puzzle.divisor}
              </span>
              <span className="text-[var(--numex-tile-idle-border)]">·</span>
              <span
                className="text-[var(--numex-muted)] underline decoration-dotted underline-offset-2 group-hover:text-[var(--numex-ink)] transition-colors"
                data-testid="view-rule"
              >
                View rule
              </span>
            </button>
          </div>

          {/* Transient toast (overlaid, does not push layout) */}
          {message && (
            <div
              className="numex-toast pointer-events-none absolute top-9 left-1/2 z-20 -translate-x-1/2 whitespace-nowrap rounded-md bg-[var(--numex-ink)] px-4 py-2 text-sm font-medium text-white shadow-md"
              data-testid="numex-message"
              role="status"
            >
              {message}
            </div>
          )}

          <div className="mt-4 w-full">
            <Grid
              rowCount={puzzle.max_attempts}
              colCount={puzzle.length}
              guesses={guesses}
              currentGuess={currentGuess}
              revealRow={revealRow}
              shakeRow={shakeRow}
            />
          </div>
        </main>

        {/* Keyboard */}
        <div className="w-full mt-5">
          <Keyboard
            onKey={handleKey}
            onDelete={handleDelete}
            onEnter={handleEnter}
            keyStates={{}}
            disabled={locked}
          />
        </div>
      </div>

      <Footer />

      <HowToPlay
        open={howToOpen}
        onOpenChange={setHowToOpen}
        onPlay={dismissOnboarding}
        divisor={puzzle.divisor}
      />
      <DivisibilityInfo open={ruleOpen} onOpenChange={setRuleOpen} divisor={puzzle.divisor} />
      <ResultModal
        open={resultOpen}
        onOpenChange={setResultOpen}
        status={status}
        puzzleNumber={puzzle.puzzle_number}
        divisor={puzzle.divisor}
        attempts={guesses.length}
        maxAttempts={puzzle.max_attempts}
        answer={answer}
        guesses={guesses}
        stats={stats}
      />
    </div>
  );
}
